/// <amd-module name="Repos/CommitsList/Components/HistoryGraphCell.Props" />
import { GitCommitRef } from "Repos/Common/Contracts/Git";
import { ObservableValue } from "VSS/Core/Observable";

export type IDictionaryNumberTo<T> = { [key: number]: T };
export type IDictionaryStringTo<T> = { [key: string]: T };

/**
 * Enum of all the visualization components that can be drawn on a cell. Assigned powers of 2 to support bit flags.
 */
export enum VisComponents {
    /*
    This denotes an empty cell with no component in it marked for render
    +-----+
    |     |
    |     |
    |     |
    |     |
    +-----+
    */
    None = 0,

    /*
    +-----+
    |\    |
    | \   |
    |     |
    |     |
    +-----+
    */
    TopLeft = 1,

    /*
    +-----+
    |  |  |
    |  |  |
    |     |
    |     |
    +-----+
    */
    TopMiddle = 2,

    /*
    +-----+
    |    /|
    |   / |
    |     |
    |     |
    +-----+
    */
    TopRight = 4,

    /*
    +-----+
    |     | 
    |__   |
    |     |
    |     |
    +-----+
    */
    LeftCenter = 8,

    /*
    +-----+
    |     | 
    |     |
    |   \ |
    |    \|
    +-----+
    */
    BottomRight = 16,

    /*
    +-----+
    |     | 
    |     |
    |  |  |
    |  |  |
    +-----+
    */
    BottomMiddle = 32,

    /*
    +-----+
    |     | 
    |     |
    | /   |
    |/    |
    +-----+
    */
    BottomLeft = 64,

    /*
    +-----+
    |     | 
    |   __|
    |     |
    |     |
    +-----+
    */
    RightCenter = 128,

    /* This denotes that the cell holds a commit node*/
    Circle = 256,

    /*
    +-----+
    |     | 
    |_    |
    | \   |
    |     |
    +-----+
    */
    LeftMerge = 512,

    /*
    +-----+
    |     | 
    |    _|
    |   / |
    |     |
    +-----+
    */
    RightMerge = 1024,

    /*
    +-----+
    |     | 
    |     |
    |     |
    |  |  |
    +-----+
    */
    OctopusMerge = 2048,

    /* This denotes that the cell holds a merge node*/
    MergeNode = 4096
}

/**
 * Enum of all the excision visualization components that can be drawn on a cell. Assigned powers of 2 to support bit flags.
 */
export enum ExcisionVisComponents {
    /*
    This denotes an empty cell with no excision component in it marked for render
    +-----+
    |     |
    |     |
    |     |
    |     |
    +-----+
    */
    None = 0,

    /*
    +-----+
    |     | 
    |    _|
    |   / |
    |   | |
    +-----+
    */
    OutgoingExcision = 1,

    /*
    +-----+
    | |   | 
    | \ __|
    |     |
    |     |
    +-----+
    */
    IncomingExcision = 2,

    /*
    +-----+
    |     | 
    |   __|
    |  /  |
    |  |  |
    +-----+
    */
    OutgoingSelectedExcision = 4,

    /*
    +-----+
    |  |  | 
    |  \__|
    |     |
    |     |
    +-----+
    */
    IncomingSelectedExcision = 8,

    /*
    +-----+
    |  |  | 
    |  |  |
    |  |  |
    |  |  |
    +-----+
    */
    ContinuingSelectedTrackingLine = 16,

    /*
    +-----+
    |     | 
    |_____|
    |     |
    |     |
    +-----+
    */
    ExcisionHorizontal = 32
}

/**
 * Enum of all the highlight directions used for highlighting graph rows
 */
export enum HighlightDirection {
    All,
    FromAbove,
    FromBelow,
    SelectionRow
}

export interface VisualizationCell {
    components: VisComponents;
    excisionComponents: ExcisionVisComponents;
    highlitComponents: VisComponents;
    highlitExcisionComponents: ExcisionVisComponents;
    id: number;
}

/**
 * Renderer to support in drawing components into the graph cell canvas
 */
export interface IHistroyGraphCellRenderer {
    cleanCell(canvasContext: CanvasRenderingContext2D, canvasStaticWidth: number, canvasStaticHeight: number, canvasExtendedHeight?: number): void;
    drawCell(
        cell: VisualizationCell,
        canvasContext: CanvasRenderingContext2D,
        canvasStaticWidth: number,
        canvasStaticHeight: number,
        canvasExtendedHeight?: number
    ): void;
}
/**
 * Enum of all the orientation directions used for graph rendering
 */
export enum HistoryGraphOrientation {
    /*
        The commit dots start occupying the cells in rows from the left most and the graph grows rightward
        The excisions and tracing cells are present in the left of the graph cells
    */
    LeftJustify,
    /*
        The commit dots start occupying the cells in rows from the right most and the graph grows leftward
        The excisions and tracing cells are present to the right of the graph cells
    */
    RightJustify
}

/**
 * Settings pertaining to rendering of graph nodes and lines
 */
export interface IHistoryGraphRenderSettings {
    /* Node settings */
    mergeColor: string;
    mergeFillColor: string;
    mergeHighlightColor: string;
    mergeHighlightFillColor: string;
    mergeHighlightRadius: number;
    mergeRadius: number;
    mergeStrokeWidth: number;
    nodeColor: string;
    nodeFillColor: string;
    nodeHighlightColor: string;
    nodeHighlightFillColor: string;
    nodeHighlightRadius: number;
    nodeStrokeWidth: number;
    nodeRadius: number;

    /* Line settings */
    lineColor: string;
    lineHighlightColor: string;
    lineHighlightWidth: number;
    lineWidth: number;

    /* Cell settings */
    cellStaticHeight: number;
    cellStaticWidth: number;
}

/**
 * Settings pertaining to the git history graph
 */
export interface IHistoryGraphSettings {
    orientation: HistoryGraphOrientation;
    excisionSettings: IHistoryGraphExcisionSettings;
}

/**
 * Settings pertaining to graph excisions
 */
export interface IHistoryGraphExcisionSettings {
    emptyLineLengthLimit: number;
    emptyLineLengthMultiplier: number;
    excisionEnabled: boolean;
}

/**
 * Model for git history graph
 */
export interface IHistoryGraph {
    rows: IDictionaryStringTo<ObservableValue<IHistoryGraphRow>>;
    rowIdCommitIdMap: IDictionaryNumberTo<string>;
    settings: IHistoryGraphSettings;
    selectedCommitId: ObservableValue<string | null>;
    selectedExcisedParentCommitIds: string[];
    selectedExcisedChildCommitIds: string[];
    selectedOldestExcisedParentRowId: number | null;
    selectedYoungestExcisedChildRowId: number | null;

    /* Functions */
    select(selectedCommitId: string): void;
    unSelect(): void;
}

/**
 * Model for a row in the history graph
 */
export interface IHistoryGraphRow {
    aboveCommitId: string;
    belowCommitId: string;
    cells: IDictionaryNumberTo<VisualizationCell>;
    childCommitIds: string[];
    commit: GitCommitRef;
    commitCellId: number;
    hasOutgoingExcisedCommits: boolean;
    hasIncomingExcisedCommits: boolean;
    hasOngoingExcisedCommitTrackingLine: boolean;
    id: number;
    isSelectedExcisedCommitParent: boolean;
    isSelectedExcisedCommitChild: boolean;
    isSelected: boolean;
    maxCellId: number;
    parentCommitIds: string[];
    parentGraph: IHistoryGraph;

    /* Functions */
    getExcisionCell(): VisualizationCell;
    getTracingCell(): VisualizationCell;
    highlightHorizontal(
        commitCellId: number,
        highlightDirection: HighlightDirection,
        highlightCentreAndAbove: boolean,
        highlightCentreAndBelow: boolean
    ): void;
    highlightFromAbove(cellsAbove: IDictionaryNumberTo<VisualizationCell>): void;
    highlightFromBelow(cellsBelow: IDictionaryNumberTo<VisualizationCell>): void;
    unHighlight(): void;
}
